import { motion } from "framer-motion";
import {
  FileBarChart,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Clock,
  Download,
} from "lucide-react";
import type { AuditId } from "@/packages/types";
import { List, Badge, springs } from "@/components/ui";
import { useTranslation } from "@audit-app/utils";
import { ReportRowMenu } from "./ReportRowMenu";

export interface ReportView {
  id: string;
  auditId: AuditId;
  auditName: string;
  auditCode: string | null;
  auditStatus: string;
  generatedAt: number;
  status: string;
  isDraft: boolean;
  version: number;
  pdfUrl: string | null;
}

const STATUS_CONFIG: Record<
  string,
  { dotColor: string; icon: React.ReactNode }
> = {
  ready: {
    dotColor: "#4ade80",
    icon: <CheckCircle2 size={12} className="text-green-400" />,
  },
  generating: {
    dotColor: "var(--color-accent)",
    icon: <Loader2 size={12} className="animate-spin text-accent" />,
  },
  failed: {
    dotColor: "#f87171",
    icon: <AlertCircle size={12} className="text-red-400" />,
  },
};

const MAX_STAGGER_INDEX = 12;
const STAGGER_STEP = 0.03;

interface ReportListViewProps {
  groupedReports: Map<string, ReportView[]>;
  onRowClick: (id: string) => void;
  onDownload: (report: ReportView) => void;
  onViewAudit: (auditId: string) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  selectedIds?: Set<string>;
}

function ReportRowContent({
  report,
  index,
  onDownload,
  onViewAudit,
}: {
  report: ReportView;
  index: number;
  onDownload: (report: ReportView) => void;
  onViewAudit: (auditId: string) => void;
}) {
  const { t } = useTranslation(["reports"]);
  const config = STATUS_CONFIG[report.status] ?? STATUS_CONFIG.ready;
  const generatedDate = new Date(report.generatedAt).toLocaleDateString();

  return (
    <motion.div
      className="flex items-center gap-3 w-full"
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        ...springs.snappy,
        delay: Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP,
      }}
    >
      {/* Icon */}
      <div className="shrink-0 p-2 rounded-lg bg-accent/10">
        <FileBarChart size={16} className="text-accent" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Line 1: audit name + status + draft badge */}
        <div className="flex items-center gap-2 min-w-0">
          {report.auditCode && (
            <span className="text-[11px] text-muted font-mono shrink-0 truncate max-w-36">
              {report.auditCode}
            </span>
          )}

          <p className="text-sm font-semibold text-primary truncate shrink min-w-0">
            {report.auditName}
          </p>

          <div className="flex items-center gap-1.5 shrink-0">
            <Badge
              color={{
                bg:
                  report.status === "ready"
                    ? "rgba(74,222,128,0.15)"
                    : report.status === "failed"
                      ? "rgba(248,113,113,0.15)"
                      : "color-mix(in oklch, var(--color-accent) 15%, transparent)",
                text: config.dotColor,
              }}
            >
              <span className="inline-flex items-center gap-1">
                {config.icon}
                {t(`reports:status.${report.status}` as "reports:status.ready")}
              </span>
            </Badge>

            {report.isDraft && (
              <Badge color={{ bg: "rgba(161,161,166,0.15)", text: "#a1a1a6" }}>
                {t("reports:card.draft")}
              </Badge>
            )}
          </div>
        </div>

        {/* Line 2: date + version */}
        <div className="flex items-center gap-2 mt-0.5 min-w-0">
          <span className="flex items-center gap-1 text-[11px] text-muted shrink-0">
            <Clock size={10} className="shrink-0" />
            {generatedDate}
          </span>

          {report.version && (
            <span className="text-[11px] text-muted shrink-0">
              v{report.version}
            </span>
          )}
        </div>
      </div>

      {/* Download button */}
      {report.status === "ready" && report.pdfUrl && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDownload(report);
          }}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-accent border border-accent/30 hover:bg-accent/10 transition-colors"
        >
          <Download size={13} />
          {t("reports:list.rowMenu.download")}
        </button>
      )}

      {/* Row menu */}
      <div className="shrink-0">
        <ReportRowMenu
          hasPdf={report.status === "ready" && !!report.pdfUrl}
          onDownload={() => onDownload(report)}
          onViewAudit={() => onViewAudit(report.auditId)}
        />
      </div>
    </motion.div>
  );
}

export function ReportListView({
  groupedReports,
  onRowClick,
  onDownload,
  onViewAudit,
  onSelectionChange,
  selectedIds,
}: ReportListViewProps) {
  const { t } = useTranslation(["reports"]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={springs.snappy}
      className="h-full overflow-auto"
    >
      <List.Provider
        onRowClick={onRowClick}
        onSelectionChange={onSelectionChange}
        selectedIds={selectedIds}
      >
        <List>
          {Array.from(groupedReports.entries()).map(([status, reports]) => {
            if (reports.length === 0) return null;
            const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.ready;

            return (
              <List.Group
                key={status}
                title={t(`reports:status.${status}` as "reports:status.ready")}
                count={reports.length}
                icon={
                  <span
                    className="h-2 w-2 rounded-full inline-block"
                    style={{ backgroundColor: config.dotColor }}
                  />
                }
              >
                {reports.map((report, i) => (
                  <List.Row key={report.id} id={report.id}>
                    <ReportRowContent
                      report={report}
                      index={i}
                      onDownload={onDownload}
                      onViewAudit={onViewAudit}
                    />
                  </List.Row>
                ))}
              </List.Group>
            );
          })}
        </List>
      </List.Provider>
    </motion.div>
  );
}
